import { classNames } from 'primereact/utils';
import { Button, Col, Row } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useFetchDefectSegmentsQuery, useFetchRoadMeasuresQuery } from '../../api/rtk/base/slices/diagnosticsApiSlice';
import { useFetchObjectDocumentsQuery } from '../../api/rtk/base/slices/documentsApiSlice';
import { useFetchRoadAccidentWeatherConditionsQuery } from '../../api/rtk/base/slices/roadAccidentsApiSlice';
import { tableObjectApiSlice } from '../../api/rtk/base/slices/tableObjectApiSlice';
import { getItemWebStorage } from '../../features/webStorageSaver/webStorageSaver';
import type { tableObjectInfoSectionProps } from '../../models';
import { setCurrentRoadDiagnosticsSegment } from '../../stateManagement/reducers/diagnostics/DiagnosticsSlice';
import { setDocuments } from '../../stateManagement/reducers/DocumentsSlice';
import { store } from '../../stateManagement/store';
import { formatObject } from '../../features/dataFormatFunctions/formatObject';
import { useFetchTableValueQuery } from '../../api/rtk/base/slices/tablesApiSlice';
const square_check = require('../../assets/images/fontAwesomeIcons/square-check-regular.svg').default
const square_cross = require('../../assets/images/fontAwesomeIcons/square-xmark-solid.svg').default

// Компонент - информация об объекте на странице
export function ObjectInfoCardSection({ sectionName, sectionItems, data, eventId, pathId, showOnMapFlag }: tableObjectInfoSectionProps) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { objectId, objectTypeName }: any = useParams();
    const addressTypes = getItemWebStorage('addressTypes')
    let urlType = addressTypes.value.find((type: any) => type.name === objectTypeName) != null ? `address` : `objects`

    const {
        data: roadMeasures,
        isLoading: isLoadingRoadMeasures,
        isError: isErrorRoadMeasures
    } = useFetchRoadMeasuresQuery(
        {
            pathId,
            top: 1,
            pages: true,
            filterString: `EventId eq ${eventId}`,
            skip: 0,
            order: ''
        },
        { skip: objectTypeName !== 'RoadDiagnosticsSegments' })

    const {
        data: defectSegments,
        isLoading: isLoadingDefectSegments,
        isError: isErrorDefectSegments
    } = useFetchDefectSegmentsQuery(
        {
            top: 1,
            pages: true,
            filterStringArray: [`EventID eq ${eventId}`],
            skip: 0,
            order: ''
        },
        { skip: objectTypeName !== 'RoadDiagnosticsSegments' })

    const { data: landSubLots, isFetching: isFetchingLandSubLots, isError: isErrorLandSubLots } = useFetchTableValueQuery({
        tableValueUrl: "objects/LandSubLots",
        filterStringArray: [`ParentLotId eq ${objectId}`],
        pages: true
    }, { skip: objectTypeName !== 'RoadLandLots' && objectTypeName !== 'AdjacentLandLots' })

    const { data: landSubLotObjects, isFetching: isFetchingLandSubLotObjects, isError: isErrorLandSubLotObjects } = useFetchTableValueQuery({
        tableValueUrl: "objects/LandSubLotObjects",
        filterStringArray: [`ParentLotId eq ${objectId}`],
        pages: true
    }, { skip: objectTypeName !== 'RoadLandLots' && objectTypeName !== 'AdjacentLandLots' })

    const { data: encumbrances, isFetching: isFetchingEncumbrances, isError: isErrorEncumbrances } = useFetchTableValueQuery({
        tableValueUrl: "objects/Encumbrances",
        filterStringArray: [`LandLotId eq ${objectId}`],
        pages: true
    }, { skip: objectTypeName !== 'RoadLandLots' && objectTypeName !== 'AdjacentLandLots' })

    const { data: landSingleOwners, isFetching: isFetchingLandSingleOwners, isError: isErrorLandSingleOwners} = useFetchTableValueQuery({
        tableValueUrl: "objects/LandSingleOwners",
        filterStringArray: [`LandLotId eq ${objectId}`],
        pages: true
    }, { skip: objectTypeName !== 'RoadLandLots' && objectTypeName !== 'AdjacentLandLots' })

    const { data: landOwners, isFetching: isFetchingLandOwners, isError: isErrorLandOwners} = useFetchTableValueQuery({
        tableValueUrl: "objects/LandOwners",
        filterStringArray: [`LandLotId eq ${objectId}`],
        pages: true
    }, { skip: objectTypeName !== 'RoadLandLots' && objectTypeName !== 'AdjacentLandLots' })

    const { data: landOtherUsers, isFetching: isFetchingLandOtherUsers, isError: isErrorLandOtherUsers} = useFetchTableValueQuery({
        tableValueUrl: "objects/LandOtherUsers",
        filterStringArray: [`LandLotId eq ${objectId}`],
        pages: true
    }, { skip: objectTypeName !== 'RoadLandLots' && objectTypeName !== 'AdjacentLandLots' })

    const { data: weatherConditions } = useFetchRoadAccidentWeatherConditionsQuery({ id: objectId }, { skip: objectTypeName !== 'RoadAccidents' })

    // получение текущего токена из хранилища
    const token = store.getState().auth.tokens.app
    const currentApiUrl = getItemWebStorage("currentApiUrl")

    // запрос на получение документов объекта
    const { data: documents, isLoading: loadingDocuments } = useFetchObjectDocumentsQuery({ urlType, objectType: objectTypeName, objectId }, { skip: !data['DocumentLinks@xdata.proxy'] })
    // сохранение документов объекта в хранилище
    documents && dispatch(setDocuments(documents.value))
    // форматирование данных объекта
    data = formatObject(data)

    let documentButtonClassName = documents && classNames({
        'document-but': true,
        'table-object-info-document-but': documents?.value.length > 0,
        'table-object-info-no-document': documents?.value.length === 0
    })

    let roadDiagnosticsButtonClassName = roadMeasures && classNames({
        'on-map-but': true,
        'table-object-info-document-but': roadMeasures?.['@xdata.count'] > 0 || defectSegments?.['@xdata.count'] > 0,
        'table-object-info-no-document': roadMeasures?.['@xdata.count'] === 0 && defectSegments?.['@xdata.count'] === 0
    })

    let roadLandLotCompositionButtonClassName = classNames({
        'on-map-but': true,
        'table-object-info-document-but': landSubLots?.['@xdata.count'] > 0 || landSubLotObjects?.['@xdata.count'] > 0,
        'table-object-info-no-document': landSubLots?.['@xdata.count'] === 0 && landSubLotObjects?.['@xdata.count'] === 0
    })

    let roadLandLotEncumbrancesButtonClassName = classNames({
        'on-map-but': true,
        'table-object-info-document-but': encumbrances?.['@xdata.count'] > 0,
        'table-object-info-no-document': encumbrances?.['@xdata.count'] === 0
    })

    let roadLandLotOwnersButtonClassName = classNames({
        'on-map-but': true,
        'table-object-info-document-but': landSingleOwners?.['@xdata.count'] > 0 || landOwners?.['@xdata.count'] > 0 || landOtherUsers?.['@xdata.count'] > 0,
        'table-object-info-no-document': landSingleOwners?.['@xdata.count'] === 0 && landOwners?.['@xdata.count'] === 0 && landOtherUsers?.['@xdata.count'] === 0
    })

    function goToDiagnosticsSegmentComposition() {
        dispatch(setCurrentRoadDiagnosticsSegment({
            date: null,
            position: null,
            executor: null,
            length: null,
            path: null,
            road: null,
            start: null,
            startKmPlus: null,
            end: null,
            endKmPlus: null
        }))
        navigate(`/app/diagnostics/diagnosticsSegments/${eventId}/composition`)
    }

    function goToRoadLandLotComposition() {
        navigate(`composition`)
    }

    function goToRoadLandLotEncumbrances() {
        navigate(`encumbrances`)
    }

    function goToRoadLandLotOwners() {
        navigate(`owners`)
    }

    return (
        <>
            <Row>
                <div className="section-name">{sectionName}</div>
            </Row>
            <Row xs={1} md={3} lg={5}>
                {Object.keys(sectionItems).map((key) => {
                    let keyText = sectionItems[key]
                    let keySuper, textSuper = false
                    let beforeKeySup, keySup, beforeTextSup, textSup
                    let text = data[key]
                    // Обработчик наличия superscript текста
                    if (text) {
                        if (text?.toString().indexOf('<sup>') !== -1) {
                            const parts = text.split(/<sup>|<\/sup>/);
                            beforeTextSup = parts[0]; // Текст до <sup>
                            textSup = parts[1]; // Текст между <sup> и </sup>
                            textSuper = true
                        }
                    }
                    if (keyText) {
                        if (keyText?.toString().indexOf('<sup>') !== -1) {
                            const parts = keyText.split(/<sup>|<\/sup>/);
                            beforeKeySup = parts[0]; // Текст до <sup>
                            keySup = parts[1]; // Текст между <sup> и </sup>
                            keySuper = true
                        }
                    }
                    return (
                        <>
                            {/* Обработка отображения изображения знака */}
                            {key === 'SignRoadImage' &&
                                <>
                                    <Col className="field">
                                        <div className="sign-road-image-div">
                                            <img className="sign-road-image" alt="Отсутствует изображение" src={`${currentApiUrl}/objects/roadSignImage?Token='${token}'&Id=${objectId}&Width=300&Height=300`} />
                                        </div>
                                    </Col>
                                    <Col className="value-template"></Col>
                                </>
                            }
                            {(key === 'WeatherConditionString' && weatherConditions?.value) &&
                                <>
                                    <Col className="field">{keyText}</Col>
                                    {weatherConditions?.value?.length > 0
                                        ?
                                        <>
                                            <Col className="value" style={{ color: '#666' }}>{weatherConditions?.value.map((item: any) => item.WeatherConditionDescription).join(', ')}</Col></>
                                        :
                                        <>
                                            <Col className="value" style={{ color: "#666", opacity: '50%' }}>не указано</Col>
                                        </>
                                    }
                                </>
                            }
                            {(key !== 'SignRoadImage' && key !== 'WeatherConditionString') &&
                                <>
                                    {/* два варианта отображения названия поля: при наличии superscript / без superscript */}
                                    {keySuper
                                        ?
                                        <Col className="field">{beforeKeySup}<sup>{keySup}</sup></Col>
                                        :
                                        <Col className="field">{keyText}</Col>
                                    }
                                    {/* два варианта отображения значения: при наличии superscript / без superscript */}
                                    {textSuper
                                        ?
                                        <Col className="value">{beforeTextSup}<sup>{textSup}</sup></Col>
                                        :
                                        <>
                                            {typeof text === 'boolean' &&
                                                <>
                                                    {text === true &&
                                                        <Col className="value">
                                                            <img src={square_check} alt="true" />
                                                        </Col>
                                                    }
                                                    {text === false &&
                                                        <Col className="value">
                                                            <img src={square_cross} alt="false" />
                                                        </Col>
                                                    }
                                                </>
                                            }
                                            {typeof text !== 'boolean' &&
                                                <Col className="value">{text}</Col>
                                            }
                                        </>
                                    }
                                </>
                            }
                        </>
                    )
                })}
                {/* Обработка наличия документов у данного типа объектов */}
                {sectionName === "Местоположение на дороге" && data['DocumentLinks@xdata.proxy'] &&
                    <>
                        <Col className="field">
                            {/* обработка состояния кнопки в зависимости от наличия документов у конкретного объекта */}
                            {loadingDocuments
                                ?
                                <>
                                    <Button className="document-but table-object-info-no-document">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        <span>Загрузка...</span>
                                    </Button>
                                </>
                                :
                                <Button disabled={documents?.value.length <= 0} onClick={() => navigate(`documents`)} className={documentButtonClassName}>
                                    <span>Документы ({documents?.value.length ?? 0})</span>
                                </Button>
                            }
                        </Col>
                        <Col className="value-template"></Col>
                    </>
                }
                {/* Обработка наличия у данного объекта отображения на карте */}
                {(sectionName === "Местоположение на дороге" || sectionName === "Дополнительный участок съезда") && showOnMapFlag &&
                    <>
                        <Col className="field">
                            <Button className="table-object-info-document-but on-map-but" onClick={() => navigate(`/app/map/${objectTypeName}/${objectId}`)}>
                                Показать на карте
                            </Button>
                        </Col>
                        <Col className="value-template"></Col>
                    </>
                }
                {/* Обработка наличия у данного объекта состава диагностики */}
                {sectionName === "Участок диагностики" &&
                    <>
                        <Col className="field">
                            {(isLoadingRoadMeasures || isLoadingDefectSegments)
                                ?
                                <>
                                    <Button className="on-map-but table-object-info-no-document">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        <span>Загрузка...</span>
                                    </Button>
                                </>
                                :
                                <>
                                    {roadMeasures && defectSegments &&
                                        <Button disabled={!roadMeasures?.['@xdata.count'] && !defectSegments?.['@xdata.count']} className={roadDiagnosticsButtonClassName} onClick={goToDiagnosticsSegmentComposition}>
                                            <span>Состав диагностики ({roadMeasures?.['@xdata.count'] ?? 0}) {defectSegments?.['@xdata.count'] ? `+ Дефекты (${defectSegments?.['@xdata.count']})` : ''}</span>
                                        </Button>
                                    }
                                    {isErrorRoadMeasures && isErrorDefectSegments &&
                                        <Button disabled className={'table-object-info-document-but on-map-but'} >
                                            <span>Состав диагностики (Ошибка загрузки)</span>
                                        </Button>
                                    }
                                </>
                            }
                        </Col>
                        <Col className="value-template"></Col>
                    </>
                }
                {sectionName === "Земельный участок" &&
                    <>
                        <Col className="field">
                            {(isFetchingLandSubLots || isFetchingLandSubLotObjects)
                                ?
                                <>
                                    <Button className="on-map-but table-object-info-no-document">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        <span>Загрузка...</span>
                                    </Button>
                                </>
                                :
                                <>
                                    {landSubLots && landSubLotObjects &&
                                        <Button disabled={!landSubLots?.['@xdata.count'] && !landSubLotObjects?.['@xdata.count']} className={roadLandLotCompositionButtonClassName} onClick={goToRoadLandLotComposition}>
                                            <span>Состав ЗУ</span>
                                        </Button>
                                    }
                                    {isErrorLandSubLots && isErrorLandSubLotObjects &&
                                        <Button disabled className={'table-object-info-document-but on-map-but'} >
                                            <span>Состав ЗУ (Ошибка загрузки)</span>
                                        </Button>
                                    }
                                </>
                            }
                        </Col>
                        <Col className="value-template"></Col>
                    </>
                }
                {sectionName === "Земельный участок" &&
                    <>
                        <Col className="field">
                            {isFetchingEncumbrances
                                ?
                                <>
                                    <Button className="on-map-but table-object-info-no-document">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        <span>Загрузка...</span>
                                    </Button>
                                </>
                                :
                                <>
                                    {encumbrances &&
                                        <Button disabled={!encumbrances?.['@xdata.count']} className={roadLandLotEncumbrancesButtonClassName} onClick={goToRoadLandLotEncumbrances}>
                                            <span>Ограничения (обременения)</span>
                                        </Button>
                                    }
                                    {isErrorEncumbrances &&
                                        <Button disabled className={'table-object-info-document-but on-map-but'} >
                                            <span>Ограничения (обременения) (Ошибка загрузки)</span>
                                        </Button>
                                    }
                                </>
                            }
                        </Col>
                        <Col className="value-template"></Col>
                    </>
                }
                {sectionName === "Земельный участок" &&
                    <>
                        <Col className="field">
                            {(isFetchingLandSingleOwners || isFetchingLandOwners || isFetchingLandOtherUsers)
                                ?
                                <>
                                    <Button className="on-map-but table-object-info-no-document">
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        <span>Загрузка...</span>
                                    </Button>
                                </>
                                :
                                <>
                                    {(landSingleOwners || landOwners || landOtherUsers) &&
                                        <Button disabled={!landSingleOwners?.['@xdata.count'] && !landOwners?.['@xdata.count'] && !landOtherUsers?.['@xdata.count']} className={roadLandLotOwnersButtonClassName} onClick={goToRoadLandLotOwners}>
                                            <span>Права на ЗУ</span>
                                        </Button>
                                    }
                                    {isErrorLandSingleOwners && isErrorLandOwners && isErrorLandOtherUsers &&
                                        <Button disabled className={'table-object-info-document-but on-map-but'} >
                                            <span>Права на ЗУ (Ошибка загрузки)</span>
                                        </Button>
                                    }
                                </>
                            }
                        </Col>
                        <Col className="value-template"></Col>
                    </>
                }
            </Row>
        </>
    );
}

